"use client";
import { useState, useEffect, useMemo, Suspense } from 'react';
import QuizCard from '../components/QuizCard';
import { getQuizzesList, getAttemptsSummary, getLeaderboard } from '../helpers/quizApi';
import { hasPassedQuiz } from '../helpers/quizPassing';
import { normalizeQuizFlag } from '../helpers/quizBooleans';
import { quizzes as fallbackQuizzes } from '../quizzesData';
import LazyComponent from '@/app/components/LazyComponent';
import Loader from '@/app/components/loader/loader';
import { EmblaCarousel } from '@/app/components/sliders/embala';

const AVATAR_PLACEHOLDER = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 80 80"><rect fill="#e9ecef" width="80" height="80"/></svg>');

export default function QuizzesPage() {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('الكل');
  const [searchTerm, setSearchTerm] = useState('');
  const [verification, setVerification] = useState({
    total: 0,
    checked: 0,
    passed: 0,
    failed: 0,
    remaining: 0,
    errors: 0,
    isRunning: false,
    isComplete: false,
  });
  const [requiresLoginForStats, setRequiresLoginForStats] = useState(false);
  const [verifyRunId, setVerifyRunId] = useState(0);
  const [leaderboardItems, setLeaderboardItems] = useState([]);
  const [leaderboardLoading, setLeaderboardLoading] = useState(false);
  const categories = ['الكل', 'مهارات', 'أدوات'];

  // Fetch quizzes from API
  useEffect(() => {
    const fetchQuizzes = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const apiResponse = await getQuizzesList({
          per_page: 50,
          page: 1,
          search: searchTerm || undefined
        });
        
        console.log('Fetched quizzes from API:', apiResponse);
        
        // API returns { items: [...], page: 1, perPage: 50, total: 1, totalPages: 1 }
        // Extract the items array from the response
        const apiQuizzes = apiResponse?.items || (Array.isArray(apiResponse) ? apiResponse : []);
        
        // Map API response to match QuizCard expected format
        const mappedQuizzes = apiQuizzes.map(quiz => ({
          id: quiz.id, // WordPress post ID
          wordpressId: quiz.id, // Same as id for API calls
          title: quiz.title || 'بدون عنوان',
          description: quiz.excerpt || '',
          icon: 'bi-moon', // Default icon, can be enhanced later
          duration: 25, // Default, can be calculated or added to API
          questions: quiz.totalQuestions || 0,
          participants: 0, // Not in API response, can be added later
          points: 0,
          category: 'فوازير', // Default, can be added to API
          featuredImage: quiz.featuredImage,
          createdAt: quiz.createdAt,
          expirationAt: quiz.expirationAt ?? null,
          isPopular: false, // Can be determined by logic or added to API
          ramadan: normalizeQuizFlag(quiz.ramadan),
        }));
        
        setQuizzes(mappedQuizzes);
      } catch (err) {
        console.error('Error fetching quizzes:', err);
        setError(err.message);
        // Fallback to dummy data if API fails
        setQuizzes(fallbackQuizzes);
      } finally {
        setLoading(false);
      }
    };

    fetchQuizzes();
  }, [searchTerm]);

  const filteredQuizzes = useMemo(
    () => (selectedCategory === 'الكل'
      ? quizzes
      : quizzes.filter(quiz => quiz.category === selectedCategory)),
    [quizzes, selectedCategory]
  );

  const visibleRamadanQuizzes = useMemo(
    () => filteredQuizzes.filter((quiz) => quiz.ramadan),
    [filteredQuizzes]
  );

  useEffect(() => {
    let cancelled = false;

    const fetchPassedStats = async () => {
      const quizIds = visibleRamadanQuizzes.map((quiz) => Number(quiz.id)).filter((id) => Number.isInteger(id) && id > 0);
      const total = quizIds.length;
      setVerification({
        total,
        checked: 0,
        passed: 0,
        failed: 0,
        remaining: total,
        errors: 0,
        isRunning: total > 0,
        isComplete: total === 0,
      });
      setRequiresLoginForStats(false);

      if (typeof window === 'undefined' || total === 0) return;

      const token = localStorage.getItem('token');
      if (!token) {
        setRequiresLoginForStats(true);
        setVerification((prev) => ({ ...prev, isRunning: false, isComplete: false }));
        return;
      }

      let userId = null;
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        userId = payload?.data?.user?.id || payload?.sub || payload?.user_id || null;
      } catch {
        setRequiresLoginForStats(true);
        setVerification((prev) => ({ ...prev, isRunning: false, isComplete: false }));
        return;
      }
      if (!userId) {
        setRequiresLoginForStats(true);
        setVerification((prev) => ({ ...prev, isRunning: false, isComplete: false }));
        return;
      }

      try {
        const summaryResponse = await getAttemptsSummary(quizIds, userId);
        if (cancelled) return;

        const summaries = (summaryResponse && typeof summaryResponse.summaries === 'object' && summaryResponse.summaries)
          ? summaryResponse.summaries
          : {};
        const missingQuizIds = Array.isArray(summaryResponse?.missingQuizIds)
          ? summaryResponse.missingQuizIds.map((id) => Number(id)).filter((id) => Number.isInteger(id) && id > 0)
          : [];

        let passed = 0;
        let resolved = 0;
        let unresolved = 0;

        for (const quizId of quizIds) {
          const summary = summaries[String(quizId)];
          if (summary && typeof summary === 'object') {
            resolved += 1;
            if (hasPassedQuiz(summary.highestGrade, true)) passed += 1;
            continue;
          }

          // Missing from DB rows means no attempts yet, which is still a resolved state.
          if (missingQuizIds.includes(quizId)) {
            resolved += 1;
            continue;
          }

          unresolved += 1;
        }

        const failed = Math.max(resolved - passed, 0);
        const remaining = Math.max(total - resolved, 0);
        const isComplete = resolved === total;

        setVerification({
          total,
          checked: resolved,
          passed,
          failed,
          remaining,
          errors: unresolved,
          isRunning: false,
          isComplete,
        });
      } catch {
        if (cancelled) return;
        setVerification({
          total,
          checked: 0,
          passed: 0,
          failed: 0,
          remaining: total,
          errors: total > 0 ? total : 1,
          isRunning: false,
          isComplete: false,
        });
      }
    };

    fetchPassedStats();

    return () => {
      cancelled = true;
    };
  }, [visibleRamadanQuizzes, verifyRunId]);

  useEffect(() => {
    let cancelled = false;
    const fetchLeaderboard = async () => {
      setLeaderboardLoading(true);
      try {
        const res = await getLeaderboard({ type: 'ramadan', limit: 50 });
        if (cancelled) return;
        const raw = Array.isArray(res?.items) ? res.items : [];
        const excludedRoles = ['commadmin', 'administrator'];
        const isExcluded = (item) => excludedRoles.includes(item.userRole);
        const commadminCount = raw.filter((item) => isExcluded(item)).length;
        const filtered = raw.filter((item) => !isExcluded(item));
        const items = filtered
          .map((item) => {
            const excludedAhead = raw.filter((r) => isExcluded(r) && r.rank < item.rank).length;
            return { ...item, rank: item.rank - excludedAhead };
          })
          .slice(0, 10);
        setLeaderboardItems(items);
      } catch {
        if (!cancelled) setLeaderboardItems([]);
      } finally {
        if (!cancelled) setLeaderboardLoading(false);
      }
    };
    fetchLeaderboard();
    return () => { cancelled = true; };
  }, []);

  const leaderboardSlides = useMemo(() => {
    return leaderboardItems.map((item) => {
      const attempts = Number(item.totalAttempts) || 0;
      const passed = Number(item.quizzesPassed) || 0;
      const avg = Number(item.averageCompletionPct) || 0;
      const attemptsWord = attempts === 1 ? 'محاولة' : 'محاولات';
      const passedWord = passed === 1 ? 'اختبار مجتاز' : 'اختبارات مجتازة';
      return {
        id: item.userId,
        title: `${item.name} — #${item.rank}`,
        subtitle: `${passed} ${passedWord} · متوسط الإنجاز: ${avg.toFixed(1)}%`,
        // subtitle: `${attempts} ${attemptsWord} · ${passed} ${passedWord}`,
        featuredImage: { node: { sourceUrl: item.avatar || AVATAR_PLACEHOLDER } },
      };
    });
  }, [leaderboardItems]);

  return (
    <main className="container py-5" dir="rtl">
      {/* Header */}
      <div className="text-center mb-5">
        <h1
          className="display-4 fw-bold mb-3"
        >
          فوازير رمضان
        </h1>
        <p className="lead text-muted mx-auto" style={{ maxWidth: '700px' }}>
شارك معنا في مسابقة فوازير رمضان         </p>
        <div className="mt-3">
          {requiresLoginForStats ? (
            <span className="text-muted small">سجّل الدخول لمعرفة عدد الفوازير المجتازة</span>
          ) : verification.isRunning ? (
            <div className="d-flex flex-column align-items-center gap-2 text-muted small">
              <div className="d-inline-flex align-items-center gap-2">
                <div className="spinner-border spinner-border-sm text-primary" role="status" aria-hidden="true"></div>
                <span>جاري التحقق من النتائج...</span>
              </div>
              <div className="d-inline-flex flex-wrap justify-content-center gap-2">
                <span className="badge rounded-pill text-bg-secondary">تم التحقق {verification.checked}/{verification.total}</span>
                <span className="badge rounded-pill text-bg-success">المجتاز {verification.passed}</span>
                <span className="badge rounded-pill text-bg-danger">غير المجتاز {verification.failed}</span>
                <span className="badge rounded-pill text-bg-dark">المتبقي {verification.remaining}</span>
              </div>
            </div>
          ) : verification.isComplete && verification.errors === 0 ? (
            <span className="badge rounded-pill bg-success border-0 p-3 fs-6">
              اجتزت {verification.passed} من {verification.total}
            </span>
          ) : verification.errors > 0 ? (
            <div className="d-flex flex-column align-items-center gap-2">
              <div className="alert alert-warning mb-0 py-2 px-3 small" role="alert">
                تعذّر التحقق من {verification.errors} فوازير. تم التحقق من {verification.checked}/{verification.total}.
              </div>
              <button
                type="button"
                className="btn btn-outline-primary btn-sm rounded-pill px-3"
                onClick={() => setVerifyRunId((prev) => prev + 1)}
              >
                إعادة التحقق
              </button>
            </div>
          ) : (
            <span className="text-muted small">لا توجد فوازير متاحة للتحقق</span>
          )}
        </div>
      </div>

      {/* Leaderboard — after "اجتزت X من Y" */}
      {/* <div className="my-0 my-md-5 pb-5 mx-auto col-12 fade-in-up">
        <h2 className="ms-2 ms-md-2 mb-3"><i className='bi bi-trophy' /> لوحة المتصدرين ( Top 10 )</h2>
        <p className="text-muted small ms-2 ms-md-2 mt-n2 mb-3">
          في حال تساوت الإجابات ونسب الإنجاز، يتم الاحتكام إلى الوقت، الأسرع في إرسال الإجابة هو الأعلى.
        </p>
        <LazyComponent fallback={<Loader />} threshold={0.1} rootMargin="100px">
          <Suspense fallback={<Loader />}>
            {leaderboardLoading ? (
              <Loader />
            ) : leaderboardSlides.length > 0 ? (
              <EmblaCarousel
                sectionType="leaderboard"
                link="#"
                data={leaderboardSlides}
                controls={false}
                img={{ size: 'thumbnail', width: 80, height: 80, class: 'rounded-circle mb-3' }}
                count={12}
                options={{ align: 'start', containScroll: false, slidesToScroll: 'auto', direction: 'rtl', loop: false }}
                id="ramadan-leaderboard"
                classes="embla-ships ms-0"
                section={{ title: true, content: false, bg: false }}
              />
            ) : null}
          </Suspense>
        </LazyComponent>
      </div> */}

      {/* Filter Bar */}
      {/* <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-3">
        <div className="d-flex gap-2 flex-wrap">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`btn rounded-pill px-4 py-2 ${selectedCategory === category
                ? 'btn-primary'
                : 'btn-outline-secondary'
                }`}
            >
              {category}
            </button>
          ))}
        </div>
        <div className="d-flex gap-2">
          <input
            type="text"
            placeholder="بحث ..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-control rounded-pill"
            style={{ maxWidth: '250px' }}
          />
        </div>
      </div> */}

      {/* Loading State */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">جاري التحميل...</span>
          </div>
          <p className="mt-3 text-muted">جاري تحميل الفوازير...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="alert alert-warning" role="alert">
          <i className="bi bi-exclamation-triangle me-2"></i>
          {error}
          <br />
          <small>يتم عرض البيانات المحلية كبديل</small>
        </div>
      )}

      {/* Quizzes Grid */}
      {!loading && (
        <div className="row row-cols-1 row-cols-md-2 row-cols-lg-3 g-4">
          {visibleRamadanQuizzes.length > 0 ? (
            visibleRamadanQuizzes.map((quiz) => (
              <div key={quiz.id} className="col">
                <QuizCard quiz={quiz} ramadan={quiz.ramadan} />
              </div>
            ))
          ) : (
            <div className="col-12 text-center py-5">
              <p className="text-muted">لا يوجد اختبارات متاحة في هذه الفئة حالياً.</p>
            </div>
          )}
        </div>
      )}
    </main>
  );
}
